import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFragment } from "react-relay";

import { GENERATE_APP_JWT_AUTH_VIEWER_SCHEMA_FRAGMENT } from "./generate-app-jwt.graphql";
import type { GenerateAppJwtProps } from "./generate-app-jwt.types";
import { useAuthContext } from "../../hooks/use-auth-context";
import { supabase } from "../../network/supabase-client/supabase-client";
import { selectAppJwt } from "../../store/slices/AuthSlice";

export const GenerateAppJwt = ({ queryRef }: GenerateAppJwtProps) => {
	const { generateAppJwt } = useAuthContext();
	const [hasSupabaseSession, setHasSupabaseSession] = useState(false);
	const tkaBackendToken = useSelector(selectAppJwt);

	const { currentUser } = useFragment(GENERATE_APP_JWT_AUTH_VIEWER_SCHEMA_FRAGMENT, queryRef);

	const checkSupabaseSession = useCallback(async () => {
		const {
			data: { session },
		} = await supabase.auth.getSession();
		setHasSupabaseSession(!!session);
	}, []);

	useEffect(() => {
		checkSupabaseSession().catch((error: unknown) => {
			if (error instanceof Error) {
				Sentry.captureException(error, { level: "warning" });
			}
		});

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(() => {
			checkSupabaseSession().catch((error: unknown) => {
				if (error instanceof Error) {
					Sentry.captureException(error, { level: "warning" });
				}
			});
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [checkSupabaseSession]);

	useEffect(() => {
		// no supabase session; skip generating tka token
		if (!hasSupabaseSession) {
			return;
		}

		// tka token already exists; skip generating tka token
		if (tkaBackendToken) {
			return;
		}

		// no currentUser yet; skip then
		if (!currentUser) {
			return;
		}

		const userId = currentUser.user.id;
		const accountId = currentUser.accounts[0]?.id;

		if (!userId || !accountId) {
			return;
		}

		generateAppJwt(userId, accountId).catch((error: unknown) => {
			if (error instanceof Error) {
				Sentry.captureException(error, {
					extra: {
						userId,
						accountId,
					},
				});
			}
		});
	}, [hasSupabaseSession, currentUser, generateAppJwt, tkaBackendToken]);

	return null;
};
